import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, PageError, PageLoading } from "../components/index.js";
import { Field, Select } from "../components/AdminForm.jsx";
import { LineItemEditor } from "../components/LineItemEditor.jsx";
import { BillingChip } from "./AgencyBilling.jsx";
import { QuickAdd } from "./ProposalBuilder.jsx";
import { WorkStatusChip, fmtDate as workFmtDate } from "./AgencyWork.jsx";
import { useApi, useApiAction } from "../hooks/useApi.js";
import { useAuth } from "../context/AuthContext.jsx";
import { loadSession } from "../lib/storage.js";

/**
 * Contracts (/agency/contracts) — admin management surface for structured
 * client contracts (Phase 4b-1). List with read-time status chips + renewal
 * countdowns, expandable row detail (line items via the shared
 * LineItemEditor), draft lifecycle actions (activate / renew / delete), and
 * a New Contract modal whose "active" status is the direct-entry path for
 * backfilling legacy contracts (no proposal or signature required).
 *
 * Phase 4b-1.5 adds: agreement type/term/auto-renew fields with billing &
 * signer contact pickers (per-client ClientContacts + quick-add), a type
 * filter row, and "Import contract" — AI intake from a PDF or pasted text
 * that lands on a reviewable draft ("Imported" badge, collapsible
 * Extraction data panel with one-click add for suggested contacts).
 *
 * Phase 4b-1.5b (executed-page merge): the Import modal takes an optional
 * second document — the separately returned executed/signature page — merged
 * in one extraction; the detail view gains an Execution row (signed date,
 * signer, executed-doc link — editable per PUT rules on draft AND active)
 * and an "Add executed page" flow that supplements an existing contract via
 * POST /:id/intake-supplement.
 *
 * EMPLOYEEs never see this page (AdminRoute + API 403); they read contract
 * data through the client cockpit panel.
 */

const FILTERS = [
  { key: "all",      label: "All" },
  { key: "active",   label: "Active" },
  { key: "expiring", label: "Expiring" },
  { key: "draft",    label: "Draft" },
  { key: "expired",  label: "Expired" },
  { key: "unclassified", label: "Needs classification" },
];

// Change #2 — continuation behaviour. Keep in sync with services/contracts.js
// CONTINUATION_TYPES. `null` is Unclassified and must NEVER render as
// "Ends on a fixed date": it behaves as a fixed term operationally, but showing
// a human decision nobody made would defeat the review filter.
const CONTINUATION_OPTIONS = [
  { value: "FIXED_TERM", label: "Ends on a fixed date" },
  { value: "CONTINUING", label: "Continues until cancelled" },
  { value: "PROJECT",    label: "One-time project" },
];
const CONTINUATION_LABEL = Object.fromEntries(CONTINUATION_OPTIONS.map((o) => [o.value, o.label]));
// Codex #6 — mirrors the API's `details.saved`: what actually persisted when
// reconciliation did not complete. Keep in step with SAVED_LABEL in routes/contracts.js.
const SAVED_LABEL = {
  classification: "The classification",
  cancellation: "The cancellation",
  coverage_dates: "The contract changes",
  uncancellation: "The reversal",
};
const continuationLabel = (v) => (v == null ? "Unclassified" : CONTINUATION_LABEL[v] ?? v);

// Operational authority and commercial renewal are INDEPENDENT: "Active —
// continuing" and "Renewal overdue" are both true on a continuing agreement
// past its initial term.
const OPERATIONAL_LABEL = {
  PENDING: "Pending", ACTIVE: "Active", ENDING: "Ending", ENDED: "Ended",
};
const RENEWAL_LABEL = {
  NOT_APPLICABLE: null, SCHEDULED: null, APPROACHING: "Renewal approaching",
  DUE: "Renewal due", OVERDUE: "Renewal overdue", TERM_COMPLETE: "Term complete",
};
const OPERATIONAL_CHIP = {
  ACTIVE:  "bg-[#f0fdf4] border-[#bbe7c6] text-[#16a34a]",
  ENDING:  "bg-[#fbeae0] border-[#f0b890] text-[#8a4b1a]",
  ENDED:   "bg-app-bg border-border-default text-text-muted",
  PENDING: "bg-app-bg border-border-default text-text-secondary",
};
const RENEWAL_CHIP = {
  APPROACHING:   "bg-[#fbeae0] border-[#f0b890] text-[#8a4b1a]",
  DUE:           "bg-[#fbeae0] border-[#f0b890] text-[#8a4b1a]",
  OVERDUE:       "bg-[#fef2f2] border-[#f3c1c1] text-[#dc2626]",
  TERM_COMPLETE: "bg-app-bg border-border-default text-text-muted",
};

/** Operational chip + an independent renewal chip when one applies. */
function ContractStateChips({ contract }) {
  const op = contract.operationalState ?? "ACTIVE";
  const opLabel = op === "ACTIVE" && contract.effectiveContinuationType === "CONTINUING"
    ? "Active — continuing"
    : OPERATIONAL_LABEL[op] ?? op;
  const renewal = RENEWAL_LABEL[contract.renewalState];
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-medium ${OPERATIONAL_CHIP[op] ?? OPERATIONAL_CHIP.PENDING}`}>{opLabel}</span>
      {renewal && (
        <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-medium ${RENEWAL_CHIP[contract.renewalState]}`}>{renewal}</span>
      )}
      {contract.isUnclassified && (
        <span className="inline-block px-2 py-0.5 rounded-full border border-border-default bg-app-bg text-[11px] font-medium text-text-secondary">Unclassified</span>
      )}
    </span>
  );
}

// Agreement classification (4b-1.5) — keep in sync with services/contracts.js
// CONTRACT_TYPES.
const CONTRACT_TYPE_OPTIONS = [
  { value: "aor",      label: "AOR / Retainer" },
  { value: "web_dev",  label: "Web Dev" },
  { value: "seo_sow",  label: "SEO SOW" },
  { value: "hosting",  label: "Hosting" },
  { value: "social",   label: "Social" },
  { value: "other",    label: "Other" },
];
const typeLabel = (t) => CONTRACT_TYPE_OPTIONS.find((o) => o.value === t)?.label || "—";

const CONTACT_TYPE_OPTIONS = [
  { value: "regular", label: "Regular" },
  { value: "billing", label: "Billing" },
  { value: "signer",  label: "Signer" },
];

// effectiveStatus → chip palette (renewal amber/red match the cockpit badge).
const STATUS_CHIP = {
  active:    "bg-[#f0fdf4] border-[#bbe7c6] text-[#16a34a]",
  expiring:  "bg-[#fbeae0] border-[#f0b890] text-[#8a4b1a]",
  expired:   "bg-[#fef2f2] border-[#f3c1c1] text-[#dc2626]",
  draft:     "bg-app-bg border-border-default text-text-secondary",
  sent:      "bg-[#eff6ff] border-[#c3d9f5] text-[#2563eb]",
  cancelled: "bg-app-bg border-border-default text-text-muted",
  renewed:   "bg-app-bg border-border-default text-text-muted",
};

const fmtMoney = (v) => `$${Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");

// Renewal-draft repricing note (/renew's `repriced` entries have no feeType,
// so amount changes render plain — no /mo or /yr suffix).
function fmtRepriced(x) {
  const pct = (v) => `${Math.round((v ?? 0) * 100)}%`;
  if (x.field === "amount") return `${x.serviceName}: ${fmtMoney(x.from)} → ${fmtMoney(x.to)}`;
  if (x.field === "variableRate") return `${x.serviceName}: rate ${pct(x.from)} → ${pct(x.to)}`;
  if (x.field === "variableMinimum") return `${x.serviceName}: minimum ${fmtMoney(x.from)} → ${fmtMoney(x.to)}`;
  return `${x.serviceName} ${x.field} ${x.from ?? "—"} → ${x.to}`;
}

function StatusChip({ status }) {
  return (
    <span className={`inline-flex items-center h-5 px-2 rounded border text-[11px] font-medium capitalize ${STATUS_CHIP[status] || STATUS_CHIP.draft}`}>
      {status}
    </span>
  );
}

// Signature status chip (4b-4 click-to-sign) — mirrors STATUS_CHIP's palette
// convention but keyed on contract.signatureStatus, not contract.status.
const SIG_CHIP = {
  not_sent:      { bg: "#f5f5f5", fg: "#525252", label: "Not sent" },
  sent:          { bg: "#eff6ff", fg: "#1d4ed8", label: "Awaiting signature" },
  signed:        { bg: "#fffbeb", fg: "#b45309", label: "Awaiting countersign" },
  countersigned: { bg: "#f0fdf4", fg: "#15803d", label: "Executed" },
  declined:      { bg: "#fef2f2", fg: "#b91c1c", label: "Declined" },
};
function SignatureChip({ status }) {
  const c = SIG_CHIP[status] || SIG_CHIP.not_sent;
  return <span className="inline-flex items-center h-5 px-2 rounded text-[11px] font-medium" style={{ backgroundColor: c.bg, color: c.fg }}>{c.label}</span>;
}
const fmtInstant = (d) => (d ? new Date(d).toLocaleString() : "—");
const fmtDateOnlyUtc = (d) => (d ? new Date(d).toLocaleDateString(undefined, { timeZone: "UTC" }) : "—");

/** "Imported" provenance chip for intake-sourced rows (contract.source). */
function ImportedBadge({ source }) {
  if (source !== "intake") return null;
  return (
    <span className="ml-2 inline-flex items-center h-5 px-1.5 rounded border border-[#d8c7f0] bg-[#f7f2fd] text-[#7c3aed] text-[11px] font-medium">
      Imported
    </span>
  );
}

/** Amber ≤60d / red ≤30d — same thresholds as the cockpit RenewalBadge. */
function RenewalCountdown({ days }) {
  if (days == null || days > 60) return null;
  const red = days <= 30;
  const label = days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`;
  return (
    <span className={`ml-2 inline-flex items-center h-5 px-1.5 rounded border text-[11px] font-medium ${
      red ? "bg-[#fef2f2] border-[#f3c1c1] text-[#dc2626]" : "bg-[#fbeae0] border-[#f0b890] text-[#8a4b1a]"
    }`}>
      {label}
    </span>
  );
}

export function AgencyContracts() {
  const { user } = useAuth();
  const superAdmin = user?.role === "SUPER_ADMIN";
  const readOnly = user?.role === "ACCOUNTANT";
  const { data, loading, error, reload } = useApi("/api/contracts");
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);

  // Email deep link (routes/public.js's contractUrlFor): /agency/contracts?open=<id>
  // expands that row once the list has loaded, then clears the param.
  useEffect(() => {
    const id = params.get("open");
    if (id && data?.contracts?.some((c) => c.id === id)) {
      setExpandedId(id);
      setParams((p) => { p.delete("open"); return p; }, { replace: true });
    }
  }, [data]);

  const contracts = useMemo(() => data?.contracts || [], [data]);
  const filtered = useMemo(
    () =>
      contracts
        .filter((c) => filter === "all" || (filter === "unclassified" ? c.isUnclassified : c.effectiveStatus === filter))
        .filter((c) => typeFilter === "all" || c.type === typeFilter),
    [contracts, filter, typeFilter]
  );
  const counts = useMemo(() => {
    const m = { all: contracts.length };
    for (const c of contracts) m[c.effectiveStatus] = (m[c.effectiveStatus] || 0) + 1;
    m.unclassified = contracts.filter((c) => c.isUnclassified).length;
    return m;
  }, [contracts]);

  if (error) return <PageError error={error} onRetry={() => reload()} />;
  if (loading || !data) return <PageLoading />;

  return (
    <div className="max-w-[1200px] space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)}
              className={`h-7 px-2.5 rounded-card border text-[12px] font-medium transition-colors ${
                filter === f.key
                  ? "bg-accent text-white border-accent"
                  : "border-border-default text-text-secondary hover:text-text-primary"
              }`}>
              {f.label}{counts[f.key] ? ` · ${counts[f.key]}` : ""}
            </button>
          ))}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setImporting(true)}
              className="px-3 py-1.5 rounded-card border border-border-default text-[12px] font-medium text-text-secondary hover:text-text-primary transition-colors">
              Import contract
            </button>
            <button type="button" onClick={() => setAdding(true)}
              className="px-3 py-1.5 rounded-card bg-accent text-white text-[12px] font-medium">
              + New Contract
            </button>
          </div>
        )}
      </div>

      {/* Agreement-type filter row (4b-1.5) */}
      <div className="flex flex-wrap items-center gap-1.5">
        {[{ value: "all", label: "All types" }, ...CONTRACT_TYPE_OPTIONS].map((t) => (
          <button key={t.value} type="button" onClick={() => setTypeFilter(t.value)}
            className={`h-6 px-2 rounded-card border text-[11px] font-medium transition-colors ${
              typeFilter === t.value
                ? "bg-accent-bg border-accent text-text-primary"
                : "border-border-default text-text-muted hover:text-text-primary"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <Card className="p-0">
        <table className="w-full text-[12px] border-collapse" style={{ fontFeatureSettings: "'tnum'" }}>
          <thead>
            <tr className="text-left text-text-muted text-[11px] uppercase border-b border-border-light" style={{ letterSpacing: "0.5px" }}>
              <th className="px-4 py-2.5 font-medium">Client</th>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Start</th>
              <th className="px-4 py-2.5 font-medium">Renewal</th>
              <th className="px-4 py-2.5 font-medium text-right">Monthly</th>
              <th className="px-4 py-2.5 font-medium text-right">Lines</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-text-muted border-b border-border-light">
                  No contracts{filter === "all" ? " yet" : ` in ${filter}`}
                </td>
              </tr>
            )}
            {filtered.map((c) => (
              <ContractRow key={c.id} contract={c}
                expanded={expandedId === c.id}
                onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
                superAdmin={superAdmin}
                readOnly={readOnly}
                reloadList={() => reload({ silent: true })}
              />
            ))}
          </tbody>
        </table>
      </Card>

      {adding && (
        <NewContractModal
          onClose={() => setAdding(false)}
          onCreated={async () => { setAdding(false); await reload({ silent: true }); }}
        />
      )}

      {importing && (
        <ImportContractModal
          onClose={() => setImporting(false)}
          onImported={async (contractId) => {
            setImporting(false);
            // Reset filters so the fresh draft is visible, then expand it.
            setFilter("all");
            setTypeFilter("all");
            await reload({ silent: true });
            setExpandedId(contractId);
          }}
        />
      )}
    </div>
  );
}

function ContractRow({ contract: c, expanded, onToggle, superAdmin, readOnly, reloadList }) {
  const navigate = useNavigate();
  return (
    <>
      <tr onClick={onToggle}
        className={`border-b border-border-light cursor-pointer ${expanded ? "bg-accent-bg" : "hover:bg-app-bg"}`}>
        <td className="px-4 py-2.5">
          <button type="button"
            onClick={(e) => { e.stopPropagation(); navigate(`/clients/${c.clientId}`); }}
            className="font-medium text-text-primary hover:text-accent hover:underline">
            {c.client?.name || "—"}
          </button>
        </td>
        <td className="px-4 py-2.5 text-text-secondary whitespace-nowrap">{typeLabel(c.type)}</td>
        <td className="px-4 py-2.5 whitespace-nowrap">
          <StatusChip status={c.effectiveStatus} />
          {c.isUnclassified && (
            <span className="ml-1.5 inline-block px-2 py-0.5 rounded-full border border-border-default bg-app-bg text-[11px] font-medium text-text-secondary"
              title="Not yet reviewed against the signed agreement — behaves as a fixed term until classified">Unclassified</span>
          )}
          <ImportedBadge source={c.source} />
        </td>
        <td className="px-4 py-2.5 text-text-secondary">{fmtDate(c.startDate)}</td>
        <td className="px-4 py-2.5 text-text-secondary whitespace-nowrap">
          {fmtDate(c.renewalDate ?? c.endDate)}
          <RenewalCountdown days={c.daysToRenewal} />
        </td>
        <td className="px-4 py-2.5 text-text-secondary text-right">{fmtMoney(c.totalMonthlyValue)}</td>
        <td className="px-4 py-2.5 text-text-muted text-right">{c.lineItemCount ?? 0}</td>
      </tr>
      {expanded && (
        <tr className="border-b border-border-light">
          <td colSpan={7} className="px-4 py-4 bg-surface">
            <ContractDetail contractId={c.id} clientId={c.clientId} superAdmin={superAdmin} readOnly={readOnly} reloadList={reloadList} onClosed={onToggle} />
          </td>
        </tr>
      )}
    </>
  );
}

function ContractDetail({ contractId, clientId, superAdmin, readOnly, reloadList, onClosed }) {
  const api = useApiAction();
  const { data, loading, error, reload } = useApi(`/api/contracts/${contractId}`);
  // Billing schedule (4c) — the plan for this contract's BillingItems.
  const billingApi = useApi(`/api/billing/contracts/${contractId}`);
  // Work orders (4d) — opened from this contract's catalog task templates.
  const workApi = useApi(`/api/work/contracts/${contractId}`);
  // Change #4: the manual editor's catalog picker (QuickAdd keeps its own copy).
  const { data: catalogData } = useApi("/api/services?active=true", { enabled: !readOnly });
  // Client contact roster — feeds the billing/signer pickers and the
  // extraction panel's already-added check.
  const { data: contactsData, reload: reloadContacts } = useApi(`/api/clients/${clientId}/contacts`, { enabled: !readOnly });
  const contacts = contactsData?.contacts || [];
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editingDates, setEditingDates] = useState(false);
  const [supplementing, setSupplementing] = useState(false);
  const [markingSigned, setMarkingSigned] = useState(false);
  const [sending, setSending] = useState(false);
  const [renewalNote, setRenewalNote] = useState(null);
  // Codex #1: partial reconciliation is neither a clean success nor a failed save.
  const [reconcileNote, setReconcileNote] = useState(null);

  async function run(fn, { refreshList = true, close = false } = {}) {
    setBusy(true);
    setActionError(null);
    setReconcileNote(null);
    try {
      await fn();
      if (close) onClosed();
      else await reload({ silent: true });
      if (refreshList) await reloadList();
      return true;
    } catch (e) {
      // Codex #6 — SAVED, RECONCILIATION INCOMPLETE is not a failed save.
      //
      // Every coverage-changing endpoint (cancel, reschedule, uncancel, the
      // coverage-affecting PUT, classification) returns the same 422. Showing it
      // as a plain red error would tell the operator their cancellation did not
      // happen when it did, and would hide that re-submitting the SAME request is
      // what repairs billing and work. `details.saved` names what persisted.
      if (e?.details?.reason === "reconciliation_incomplete") {
        // Round Three #5 — OBEY details.repair; never blindly replay.
        //
        // A terminal transition (an /uncancel that committed, a backdated
        // /cancel that is already historical) cannot be replayed: the request
        // would refuse, or — worse — reverse a NEWER cancellation scheduled
        // since. The API names the safe command; the button runs THAT.
        const repair = e.details.repair ?? { kind: "same_request" };
        const stale = e.details.stale === true;
        setReconcileNote({
          ok: false,
          reconcileOnly: repair.kind === "reconcile",
          retry: repair.kind === "reconcile"
            ? () => api(repair.path, { method: repair.method ?? "POST" })
            : fn,
          text: `${SAVED_LABEL[e.details.saved] ?? "The change"} was saved, but billing/work reconciliation did not complete ` +
            `(billing: ${e.details.billing}, work: ${e.details.work}). Billing and work were NOT updated. ` +
            (stale ? "The contract changed while this was running, so nothing was applied from the outdated plan. " : "") +
            (repair.kind === "reconcile"
              ? "Reconcile to repair them — this only re-derives billing and work, and cannot change the agreement."
              : "Retry to repair them — re-submitting the same request is safe."),
        });
        await reload({ silent: true });
        if (refreshList) await reloadList();
        return false;
      }
      // A reversal that committed NOTHING is a different message: retry, but
      // nothing was saved either.
      if (e?.details?.reason === "uncancel_not_applied") {
        setReconcileNote({ ok: false, retry: fn, text: "The cancellation could not be reversed and nothing was changed. Retry the same request." });
        return false;
      }
      setActionError(e);
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (error) return <PageError error={error} onRetry={() => reload()} />;
  if (loading || !data) return <PageLoading label="Loading contract…" />;

  const { contract, lineItems } = data;
  const isDraft = contract.status === "draft";
  const isActive = contract.status === "active";
  const canActivate = lineItems.length > 0 && Boolean(contract.signerContactId || contract.billingContactId);
  const activateDisabledReason = canActivate ? "" : lineItems.length === 0 ? "Add a line item first" : "Pick a signer or billing contact first";
  const sig = contract.signatureStatus || "not_sent";
  const isSent = contract.status === "sent";
  const canSend = isDraft ? (lineItems.length > 0 && Boolean(contract.signerContact?.email)) : (isSent && sig === "sent");
  const sendDisabledReason = isDraft && !contract.signerContact?.email ? "Pick a signer contact with an email first" : isDraft && lineItems.length === 0 ? "Add a line item first" : "";

  // Codex #1 — a scheduled cancellation is reversible only until its final
  // covered day passes. operationalState is ENDING exactly for that window
  // (ENDED once coverage has lapsed), which is the same condition the uncancel
  // endpoint enforces server-side, so the control never appears when it would 409.
  const canUncancel = isActive && Boolean(contract.terminationEffectiveDate) && contract.operationalState === "ENDING";

  /**
   * Codex #1 — classify via the guarded PUT /:id/continuation-type.
   *
   * The endpoint can persist the classification and still fail to reconcile
   * billing/work (422, details.reason "reconciliation_incomplete"). Reporting
   * that as a plain error would tell the operator their save failed when it did
   * not, and would hide the fact that a RETRY is what repairs the derived state.
   * So that case gets its own truthful message with a one-click retry, and the
   * drawer is reloaded either way because the classification itself did land.
   */
  async function classify(current) {
    const choice = prompt(
      "How does this agreement continue?\n\n" +
        CONTINUATION_OPTIONS.map((o, i) => `  ${i + 1}. ${o.label}`).join("\n") +
        "\n\nEnter 1, 2 or 3. Read it off the signed agreement.",
      String(Math.max(1, CONTINUATION_OPTIONS.findIndex((o) => o.value === current) + 1))
    );
    if (choice == null) return;
    const picked = CONTINUATION_OPTIONS[Number(choice.trim()) - 1];
    if (!picked) return alert("Enter 1, 2 or 3.");
    const reason = prompt("Why? (optional — e.g. the clause you read)", "") ?? "";
    await submitClassification(picked.value, reason.trim());
  }

  async function submitClassification(continuationType, reason) {
    setBusy(true);
    setActionError(null);
    setReconcileNote(null);
    try {
      const r = await api(`/api/contracts/${contractId}/continuation-type`, {
        method: "PUT",
        body: JSON.stringify({ continuationType, ...(reason ? { reason } : {}) }),
      });
      if (r.changed === false) setReconcileNote({ ok: true, text: "Already classified that way — billing and work were re-reconciled and are up to date." });
      await reload({ silent: true });
      await reloadList();
    } catch (e) {
      if (e?.details?.reason === "reconciliation_incomplete") {
        // The classification IS saved; only the derived state is behind.
        // Classification is non-terminal, so same_request is the safe repair —
        // but honour whatever the API named rather than assuming.
        const repair = e.details.repair ?? { kind: "same_request" };
        setReconcileNote({
          ok: false,
          continuationType,
          reason,
          reconcileOnly: repair.kind === "reconcile",
          ...(repair.kind === "reconcile"
            ? { retry: () => api(repair.path, { method: repair.method ?? "POST" }) }
            : {}),
          text:
            `Classification saved as “${continuationLabel(continuationType)}”, but reconciliation did not complete ` +
            `(billing: ${e.details.billing}, work: ${e.details.work}). ` +
            "Billing and work were NOT updated. Retry to repair them — re-submitting the same classification is safe.",
        });
        await reload({ silent: true });
      } else {
        setActionError(e);
      }
    } finally {
      setBusy(false);
    }
  }

  async function openPdf(executed) {
    const win = window.open("", "_blank");
    try {
      const res = await fetch(`/api/contracts/${contractId}/pdf${executed ? "?executed=1" : ""}`, { headers: { Authorization: `Bearer ${loadSession()?.token ?? ""}` } });
      if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body?.error?.message || `PDF failed (HTTP ${res.status})`); }
      const url = URL.createObjectURL(await res.blob());
      if (win) win.location = url; else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { win?.close(); setActionError(e); }
  }

  return (
    <div className="space-y-4">
      {renewalNote && (
        <div className="text-[12px] rounded px-3 py-2 border" style={{ backgroundColor: "#eff6ff", borderColor: "#c3d9f5", color: "#2563eb" }}>
          {renewalNote} <button type="button" onClick={() => setRenewalNote(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}
      {/* Codex #1: classification saved / reconciliation incomplete. Amber, not
          red — the save succeeded; it is the derived state that needs a retry. */}
      {reconcileNote && (
        <div className="text-[12px] rounded px-3 py-2 border"
          style={reconcileNote.ok
            ? { backgroundColor: "#eff6ff", borderColor: "#c3d9f5", color: "#2563eb" }
            : { backgroundColor: "#fffbeb", borderColor: "#fcd34d", color: "#92400e" }}>
          {reconcileNote.text}
          {!reconcileNote.ok && (
            <button type="button" disabled={busy}
              onClick={() => (reconcileNote.retry
                ? run(reconcileNote.retry)
                : submitClassification(reconcileNote.continuationType, reconcileNote.reason))}
              className="ml-2 underline font-semibold disabled:opacity-50">
              {reconcileNote.reconcileOnly ? "Reconcile now" : "Retry reconciliation"}</button>
          )}
          <button type="button" onClick={() => setReconcileNote(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}
      {/* Change #2: operational authority and commercial renewal are independent
          axes — "Active — continuing" and "Renewal overdue" can both be true. */}
      <div className="mb-3"><ContractStateChips contract={contract} /></div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-[12px]">
          {[
            ["Type", typeLabel(contract.type)],
            ["Start", fmtDate(contract.startDate)],
            ["End", fmtDate(contract.endDate)],
            ["Renewal", fmtDate(contract.renewalDate)],
            ["Effective", fmtDate(contract.effectiveDate)],
            ["Term", contract.termMonths ? `${contract.termMonths} mo` : "—"],
            ["Continuation", continuationLabel(contract.continuationType)],
            ["Covered through", contract.coverageEndsOn ? fmtDate(contract.coverageEndsOn) : "Ongoing"],
            ["Terms", `net ${contract.paymentTermsDays ?? 30}`],
            ["Billing contact", contract.billingContact?.name || "—"],
            ["Signer", contract.signerContact?.name || "—"],
            ["Setup / Annual", `${fmtMoney(contract.totalSetupValue)} · ${fmtMoney(contract.totalAnnualValue)}/yr`],
            // Execution row (4b-1.5b)
            ["Signed", fmtDate(contract.signedAt)],
            ["Signed by", contract.signedByName
              ? `${contract.signedByName}${contract.signedByEmail ? ` · ${contract.signedByEmail}` : ""}`
              : contract.signedByEmail || "—"],
            ["Executed doc", contract.executedDocumentUrl
              ? <a href={contract.executedDocumentUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline">View ↗</a>
              : "—"],
            // Click-to-sign facts (4b-4)
            ["Signature", <span className="inline-flex items-center gap-1.5"><SignatureChip status={sig} />{sig === "sent" && contract.signLinkExpiresAt ? <span className="text-text-muted">link until {fmtDateOnlyUtc(contract.signLinkExpiresAt)}</span> : null}</span>],
            // Reopened drafts (sig === "not_sent") carry stale sent/hash facts
            // from a prior send — hide them rather than show misleading values.
            ...(sig !== "not_sent" ? [
              ["Sent to", contract.sentToEmail ? `${contract.sentToName || ""} · ${contract.sentToEmail} · ${fmtInstant(contract.sentAt)}` : "—"],
            ] : []),
            ["Countersigned", contract.countersignedAt ? `${contract.countersignedByName || "—"} · ${fmtInstant(contract.countersignedAt)}` : "—"],
            ...(sig !== "not_sent" ? [
              ["Document hash", contract.pdfHash ? <span className="font-mono text-[11px]" title={contract.pdfHash}>{contract.pdfHash.slice(0, 12)}…</span> : "—"],
            ] : []),
          ].map(([label, value]) => (
            <div key={label}>
              <div className="text-[10px] uppercase text-text-muted font-medium" style={{ letterSpacing: "0.5px" }}>{label}</div>
              <div className="text-text-primary mt-0.5">{value}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {!readOnly && (isDraft || isActive) && (
            <button type="button" onClick={() => setEditingDates((v) => !v)}
              className="h-8 px-3 rounded border border-border-default text-[12px] font-medium text-text-secondary hover:text-text-primary transition-colors">
              {editingDates ? "Close editor" : "Edit dates & terms"}
            </button>
          )}
          {!readOnly && (isDraft || isActive) && (
            <button type="button" onClick={() => setSupplementing(true)}
              className="h-8 px-3 rounded border border-border-default text-[12px] font-medium text-text-secondary hover:text-text-primary transition-colors">
              Add executed page
            </button>
          )}
          {!readOnly && (isDraft || (isSent && sig === "sent")) && (
            <button type="button" disabled={busy || !canSend} title={sendDisabledReason} onClick={() => setSending(true)}
              className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50">
              {isSent ? "Resend" : sig === "declined" ? "Send again" : "Send for signature"}
            </button>
          )}
          {!readOnly && isSent && sig === "sent" && (
            <button type="button" disabled={busy} onClick={() => { if (confirm("Reopen for revision? The signing link the client received will stop working.")) run(() => api(`/api/contracts/${contractId}/reopen`, { method: "POST" })); }}
              className="h-8 px-3 rounded border border-border-default text-[12px] font-medium text-text-secondary hover:text-text-primary">Revise</button>
          )}
          {!readOnly && isSent && sig === "signed" && superAdmin && (
            <button type="button" disabled={busy}
              onClick={() => { if (confirm(`Countersign?\n\nSigned by ${contract.signedByName} (${contract.signedByEmail}) on ${fmtInstant(contract.signedAt)} from ${contract.signatureIp || "unknown IP"}.\n\nThis activates the contract, emails the executed copy, and creates the client's portal login.`)) run(() => api(`/api/contracts/${contractId}/countersign`, { method: "POST" })); }}
              className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50">Countersign</button>
          )}
          {!readOnly && (isDraft || (isSent && sig === "sent")) && superAdmin && (
            <button type="button" disabled={busy} onClick={() => setMarkingSigned(true)}
              className="h-8 px-3 rounded border border-border-default text-[12px] font-medium text-text-secondary hover:text-text-primary">Mark signed</button>
          )}
          <button type="button" onClick={() => openPdf(contract.hasExecutedPdf)}
            className="h-8 px-3 rounded border border-border-default text-[12px] font-medium text-text-secondary hover:text-text-primary">
            {contract.hasExecutedPdf ? "Executed PDF" : "PDF"}
          </button>
          {!readOnly && isDraft && sig === "not_sent" && (
            <button type="button" disabled={busy || !canActivate} title={activateDisabledReason}
              onClick={() => { if (confirm(`Activate this contract without a signature?\n\nIt becomes the client's live agreement, starts today unless a start date is set, and its billing schedule is created now. Use "Send for signature" or "Mark signed" to record a signed agreement instead.`)) run(() => api(`/api/contracts/${contractId}/activate`, { method: "POST" })); }}
              className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
              Activate
            </button>
          )}
          {!readOnly && isActive && superAdmin && (
            <button type="button" disabled={busy}
              onClick={() => {
                const today = new Date().toISOString().slice(0, 10);
                const eff = prompt("Cancel this contract.\n\nFinal covered day (YYYY-MM-DD) — service, work and billing are authorised THROUGH this date inclusive, and cease the following day.\n\nLeave as today to cancel effective today.", today);
                if (eff == null) return;
                const effectiveDate = (eff.trim() || today);
                if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)) return alert("Enter a date as YYYY-MM-DD.");
                const reason = prompt("Reason (optional):", "") ?? "";
                if (!confirm(`Cancel effective ${effectiveDate}?

Work and billing for the FINAL COVERED PERIOD are kept. Later periods are cancelled and their scheduled billing voided. Items already invoiced or paid are untouched, and earned milestones stay releasable.`)) return;
                run(() => api(`/api/contracts/${contractId}/cancel`, { method: "POST", body: JSON.stringify({ effectiveDate, ...(reason.trim() ? { reason: reason.trim() } : {}) }) }));
              }}
              className="h-8 px-3 rounded border border-[#f3c1c1] text-[12px] font-medium text-[#dc2626] hover:bg-[#fef2f2] transition-colors disabled:opacity-50">
              Cancel contract
            </button>
          )}
          {/* Codex #1 — classify. Legacy and proposal-derived contracts carry
              continuationType = null ("Unclassified") and behave as a fixed term
              until a person reads the paper. This is the only way to set it:
              continuationType is deliberately excluded from the general PUT.
              Offered on draft and active (what the endpoint accepts), and always
              available — a same-value submit is the documented repair path for a
              previous attempt whose reconciliation failed. */}
          {!readOnly && superAdmin && (isActive || isDraft) && (
            <button type="button" disabled={busy}
              onClick={() => classify(contract.continuationType)}
              className="h-8 px-3 rounded border border-border-default text-[12px] font-medium text-text-secondary hover:bg-app-bg transition-colors disabled:opacity-50">
              {contract.isUnclassified ? "Classify agreement" : "Change continuation"}
            </button>
          )}
          {/* Codex #1 — uncancel, exposed ONLY while the termination is still
              legally reversible. Once the final covered day has passed the
              termination is historical and the endpoint 409s, so showing an
              actionable control would be a lie; the state chips still report it. */}
          {!readOnly && superAdmin && canUncancel && (
            <button type="button" disabled={busy}
              onClick={() => {
                if (!confirm(`Reverse the scheduled cancellation effective ${fmtDate(contract.terminationEffectiveDate)}?\n\nThe agreement continues as before. Billing and work resume from the current period; previously cancelled work is NOT reinstated.`)) return;
                run(() => api(`/api/contracts/${contractId}/uncancel`, { method: "POST" }));
              }}
              className="h-8 px-3 rounded border border-border-default text-[12px] font-medium text-text-secondary hover:bg-app-bg transition-colors disabled:opacity-50">
              Undo scheduled cancellation
            </button>
          )}
          {!readOnly && isActive && (
            <button type="button" disabled={busy}
              onClick={() => run(async () => {
                const r = await api(`/api/contracts/${contractId}/renew`, { method: "POST" });
                const parts = [...(r.repriced || []).map(fmtRepriced), ...(r.kept || []).filter((k) => k.reason !== "milestone keeps its project total" && k.reason !== "not a catalog item").map((k) => `${k.serviceName} kept (${k.reason})`)];
                setRenewalNote(parts.length ? `Renewal draft created. Prices from the catalog: ${parts.join(" · ")}` : "Renewal draft created with the same prices.");
              })}
              className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
              Draft renewal
            </button>
          )}
          {!readOnly && isDraft && superAdmin && (
            <button type="button" disabled={busy}
              onClick={() => run(() => api(`/api/contracts/${contractId}`, { method: "DELETE" }), { close: true })}
              className="h-8 px-3 rounded border border-[#f3c1c1] text-[12px] font-medium text-[#dc2626] hover:bg-[#fef2f2] transition-colors disabled:opacity-50">
              Delete draft
            </button>
          )}
        </div>
      </div>
      {!readOnly && isDraft && sig === "not_sent" && activateDisabledReason && (
        <p className="text-[12px] text-text-muted -mt-1">Activate is unavailable: {activateDisabledReason.charAt(0).toLowerCase() + activateDisabledReason.slice(1)}.</p>
      )}
      {!readOnly && isDraft && sendDisabledReason && (
        <p className="text-[12px] text-text-muted -mt-1">Send for signature is unavailable: {sendDisabledReason.charAt(0).toLowerCase() + sendDisabledReason.slice(1)}.</p>
      )}

      {sig === "declined" && (
        <p className="text-[12px] text-negative">
          {contract.declineReason
            ? `Declined ${fmtInstant(contract.declinedAt)}: "${contract.declineReason}"`
            : "The signer declined; edit and send again."}
        </p>
      )}

      {editingDates && (
        <ContractFieldsForm
          contract={contract}
          activeOnly={isActive}
          busy={busy}
          contacts={contacts}
          clientId={clientId}
          onContactAdded={() => reloadContacts({ silent: true })}
          onSave={async (body) => {
            const ok = await run(() => api(`/api/contracts/${contractId}`, { method: "PUT", body: JSON.stringify(body) }));
            if (ok) setEditingDates(false);
          }}
        />
      )}

      {contract.intakeData && (
        <ExtractionPanel
          contract={contract}
          clientId={clientId}
          contacts={contacts}
          onContactAdded={() => reloadContacts({ silent: true })}
        />
      )}

      <div>
        <div className="text-[11px] uppercase text-text-muted font-medium mb-1.5" style={{ letterSpacing: "0.5px" }}>
          Line items · {fmtMoney(contract.totalMonthlyValue)}/mo
        </div>
        {isDraft && !readOnly && (
          <div className="mb-3"><QuickAdd endpoint={`/api/contracts/${contractId}/quick-add`} superAdmin={superAdmin} onAdded={async () => { await reload({ silent: true }); await reloadList(); }} /></div>
        )}
        {/* Errors rethrow into the editor so its form stays open with the
            message; the detail + list refresh after each successful write. */}
        <LineItemEditor
          items={lineItems}
          editable={isDraft && !readOnly}
          services={catalogData?.services ?? []}
          onCreate={async (values) => {
            await api(`/api/contracts/${contractId}/line-items`, { method: "POST", body: JSON.stringify(values) });
            await reload({ silent: true });
            await reloadList();
          }}
          onUpdate={async (lid, values) => {
            await api(`/api/contracts/${contractId}/line-items/${lid}`, { method: "PUT", body: JSON.stringify(values) });
            await reload({ silent: true });
            await reloadList();
          }}
          onRemove={async (lid) => {
            await api(`/api/contracts/${contractId}/line-items/${lid}`, { method: "DELETE" });
            await reload({ silent: true });
            await reloadList();
          }}
        />
        {!isDraft && lineItems.length === 0 && (
          <p className="text-[11px] text-text-muted">Line items can only be edited while a contract is a draft.</p>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase text-text-muted font-medium mb-1.5 flex items-center justify-between" style={{ letterSpacing: "0.5px" }}>
          <span>Billing schedule</span>
          {superAdmin && isActive && <button type="button" disabled={busy} onClick={() => run(async () => { await api(`/api/billing/contracts/${contractId}/regenerate`, { method: "POST" }); await billingApi.reload({ silent: true }); }, { refreshList: false })} className="text-[11px] text-accent hover:underline normal-case tracking-normal">Regenerate</button>}
        </div>
        {(billingApi.data?.items?.length ?? 0) === 0 ? (
          <p className="text-[12px] text-text-muted">{isActive ? "No billing items yet — Regenerate to plan this contract." : "Billing items are created when the contract activates."}</p>
        ) : (
          <table className="w-full text-[12px]">
            <tbody>
              {billingApi.data.items.map((it) => (
                <tr key={it.id} className="border-t border-border-light">
                  <td className="py-1.5 pr-3">{it.serviceName}</td>
                  <td className="py-1.5 pr-3 font-mono">{it.periodKey}</td>
                  <td className="py-1.5 pr-3">{it.dueDate ? new Date(`${it.dueDate}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: "UTC" }) : "—"}</td>
                  <td className="py-1.5 pr-3">{it.amount == null ? "—" : `$${Number(it.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}</td>
                  <td className="py-1.5 pr-3"><BillingChip item={it} /></td>
                  <td className="py-1.5 font-mono text-text-muted">{it.qbInvoiceNumber || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase text-text-muted font-medium mb-1.5" style={{ letterSpacing: "0.5px" }}>Work</div>
        {(workApi.data?.orders?.length ?? 0) === 0 ? (
          <p className="text-[12px] text-text-muted">{isActive ? "No work orders yet — they open from the catalog task templates at activation and each period." : "Work orders open when the contract activates."}</p>
        ) : (
          <table className="w-full text-[12px]"><tbody>
            {workApi.data.orders.map((o) => (
              <tr key={o.id} className="border-t border-border-light">
                <td className="py-1.5 pr-3">{o.title}</td>
                <td className="py-1.5 pr-3"><WorkStatusChip status={o.status} overdue={o.overdue || o.taskCounts.overdue > 0} /></td>
                <td className="py-1.5 pr-3 text-text-secondary">
                  {o.ownerName ? (
                    <span className={o.ownerInactive ? "text-text-muted" : undefined}>
                      {o.ownerName}{o.ownerInactive ? " (inactive)" : ""}
                    </span>
                  ) : "Unassigned"}
                </td>
                <td className="py-1.5 pr-3 text-text-secondary">{o.taskCounts.done}/{o.taskCounts.total} tasks</td>
                <td className="py-1.5 text-text-secondary">{o.nextTask ? `next ${workFmtDate(o.nextTask.dueDate)}` : o.dueDate ? `due ${workFmtDate(o.dueDate)}` : ""}</td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>

      {contract.notes && (
        <p className="text-[12px] text-text-secondary border-t border-border-light pt-3 whitespace-pre-wrap">{contract.notes}</p>
      )}
      {actionError && (
        <p className="text-[12px] text-negative">{actionError.message || "Something went wrong."}</p>
      )}

      {supplementing && (
        <SupplementModal
          contractId={contractId}
          onClose={() => setSupplementing(false)}
          onApplied={async () => {
            setSupplementing(false);
            await reload({ silent: true });
            await reloadList();
          }}
        />
      )}

      {sending && <SendContractModal contract={contract} onClose={() => setSending(false)} onSend={async (body) => { await api(`/api/contracts/${contractId}/send`, { method: "POST", body: JSON.stringify(body) }); setSending(false); await run(async () => {}); }} />}
      {markingSigned && <MarkSignedModal contract={contract} onClose={() => setMarkingSigned(false)} onConfirm={async (body) => { await api(`/api/contracts/${contractId}/mark-signed`, { method: "POST", body: JSON.stringify(body) }); setMarkingSigned(false); await run(async () => {}); }} />}
    </div>
  );
}

/**
 * Contact picker with inline quick-add. `contacts` is the client's roster;
 * quick-add POSTs a new ClientContact and selects it.
 */
function ContactSelect({ label, contacts, value, onChange, clientId, defaultType, onContactAdded }) {
  const api = useApiAction();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function quickAdd() {
    if (!name.trim()) return setErr(new Error("Name is required"));
    setBusy(true);
    setErr(null);
    try {
      const res = await api(`/api/clients/${clientId}/contacts`, {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), type: defaultType }),
      });
      await onContactAdded?.();
      onChange(res.contact.id);
      setAdding(false);
      setName("");
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <Select label={label} value={value} onChange={onChange}
        options={[
          { value: "", label: "—" },
          ...contacts.map((c) => ({ value: c.id, label: `${c.name}${c.type !== "regular" ? ` (${c.type})` : ""}` })),
        ]} />
      {!adding ? (
        <button type="button" onClick={() => setAdding(true)}
          className="mt-1 text-[11px] text-accent hover:underline font-medium">
          + new contact
        </button>
      ) : (
        <div className="mt-1.5 flex items-center gap-1.5">
          <input type="text" placeholder="Name" value={name} autoFocus
            onChange={(e) => setName(e.target.value)}
            className="h-7 px-2 rounded border border-border-default text-[12px] text-text-primary bg-surface focus:outline-none focus:border-accent flex-1 min-w-0" />
          <button type="button" disabled={busy} onClick={quickAdd}
            className="h-7 px-2 rounded bg-accent text-white text-[11px] font-semibold disabled:opacity-50">Add</button>
          <button type="button" onClick={() => { setAdding(false); setErr(null); }}
            className="text-[11px] text-text-secondary hover:text-text-primary">Cancel</button>
        </div>
      )}
      {err && <div className="mt-1 text-[11px] text-negative">{err.message}</div>}
    </div>
  );
}

/**
 * Dates + terms editor. Active contracts only expose endDate/renewalDate/
 * notes/autoRenew/contacts — type, effectiveDate, and termMonths are frozen
 * once live (4b-1.5 PUT rules).
 */
function ContractFieldsForm({ contract, activeOnly, busy, contacts, clientId, onContactAdded, onSave }) {
  const asInput = (d) => (fmtDate(d) === "—" ? "" : fmtDate(d));
  const [form, setForm] = useState({
    type: contract.type ?? "",
    startDate: asInput(contract.startDate),
    effectiveDate: asInput(contract.effectiveDate),
    endDate: asInput(contract.endDate),
    renewalDate: asInput(contract.renewalDate),
    termMonths: contract.termMonths != null ? String(contract.termMonths) : "",
    autoRenew: !!contract.autoRenew,
    paymentTermsDays: contract.paymentTermsDays != null ? String(contract.paymentTermsDays) : "30",
    billingContactId: contract.billingContactId ?? "",
    signerContactId: contract.signerContactId ?? "",
    notes: contract.notes ?? "",
    // Execution facts (4b-1.5b) — editable on draft AND active (PUT rules).
    signedDate: asInput(contract.signedAt),
    signedByName: contract.signedByName ?? "",
    signedByEmail: contract.signedByEmail ?? "",
    executedDocumentUrl: contract.executedDocumentUrl ?? "",
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  function submit() {
    const body = {
      ...(activeOnly ? {} : {
        type: form.type || null,
        startDate: form.startDate || null,
        effectiveDate: form.effectiveDate || null,
        termMonths: form.termMonths ? Number(form.termMonths) : null,
      }),
      endDate: form.endDate || null,
      renewalDate: form.renewalDate || null,
      autoRenew: form.autoRenew,
      paymentTermsDays: form.paymentTermsDays === "" ? 30 : Number(form.paymentTermsDays),
      billingContactId: form.billingContactId || null,
      signerContactId: form.signerContactId || null,
      notes: form.notes.trim() || null,
      signedDate: form.signedDate || null,
      signedByName: form.signedByName.trim() || null,
      signedByEmail: form.signedByEmail.trim() || null,
      executedDocumentUrl: form.executedDocumentUrl.trim() || null,
    };
    onSave(body);
  }

  return (
    <div className="rounded-card border border-border-default p-3 space-y-2.5 bg-app-bg">
      {!activeOnly && (
        <div className="grid grid-cols-3 gap-2.5">
          <Select label="Type" value={form.type} onChange={set("type")}
            options={[{ value: "", label: "—" }, ...CONTRACT_TYPE_OPTIONS]} />
          <Field label="Start date" type="date" value={form.startDate} onChange={set("startDate")} />
          <Field label="Effective date" type="date" value={form.effectiveDate} onChange={set("effectiveDate")} />
        </div>
      )}
      <div className="grid grid-cols-3 gap-2.5">
        <Field label="End date" type="date" value={form.endDate} onChange={set("endDate")} />
        <Field label="Renewal date" type="date" value={form.renewalDate} onChange={set("renewalDate")} />
        {!activeOnly ? (
          <Field label="Term (months)" type="number" value={form.termMonths} onChange={set("termMonths")} />
        ) : <div />}
      </div>
      <Field label="Payment terms (days)" type="number" value={form.paymentTermsDays} onChange={set("paymentTermsDays")} />
      <div className="grid grid-cols-2 gap-2.5">
        <ContactSelect label="Billing contact" contacts={contacts} value={form.billingContactId}
          onChange={set("billingContactId")} clientId={clientId} defaultType="billing" onContactAdded={onContactAdded} />
        <ContactSelect label="Signer" contacts={contacts} value={form.signerContactId}
          onChange={set("signerContactId")} clientId={clientId} defaultType="signer" onContactAdded={onContactAdded} />
      </div>
      <label className="flex items-center gap-2 text-[12px] text-text-secondary cursor-pointer">
        <input type="checkbox" checked={form.autoRenew}
          onChange={(e) => set("autoRenew")(e.target.checked)} />
        Auto-renews
      </label>
      {/* Execution facts (4b-1.5b) — the manual path when a scan is unreadable. */}
      <div className="grid grid-cols-3 gap-2.5">
        <Field label="Signed date" type="date" value={form.signedDate} onChange={set("signedDate")} />
        <Field label="Signed by (name)" value={form.signedByName} onChange={set("signedByName")} />
        <Field label="Signed by (email)" type="email" value={form.signedByEmail} onChange={set("signedByEmail")} />
      </div>
      <Field label="Executed doc link (signature-page scan)" value={form.executedDocumentUrl}
        onChange={set("executedDocumentUrl")} placeholder="https://drive.google.com/…" />
      <Field label="Terms / notes" value={form.notes} onChange={set("notes")} />
      <button type="button" disabled={busy} onClick={submit}
        className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
        Save
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Intake review (4b-1.5)
// ─────────────────────────────────────────────────────────────────────────

const CONFIDENCE_CHIP = {
  high:   "bg-[#f0fdf4] border-[#bbe7c6] text-[#16a34a]",
  medium: "bg-[#fbeae0] border-[#f0b890] text-[#8a4b1a]",
  low:    "bg-[#fef2f2] border-[#f3c1c1] text-[#dc2626]",
};

function ConfidenceChip({ confidence }) {
  if (!confidence) return null;
  return (
    <span className={`inline-flex items-center h-5 px-2 rounded border text-[11px] font-medium capitalize ${
      CONFIDENCE_CHIP[confidence] || CONFIDENCE_CHIP.low
    }`}>
      {confidence} confidence
    </span>
  );
}

/**
 * Collapsible "Extraction data" panel for intake-sourced contracts — the
 * machine's reading of the document (from intakeData) next to the draft,
 * with one-click promotion of suggested contacts to ClientContact rows.
 */
function ExtractionPanel({ contract, clientId, contacts, onContactAdded }) {
  const api = useApiAction();
  const [open, setOpen] = useState(false);
  const [busyName, setBusyName] = useState(null);
  const [err, setErr] = useState(null);

  const ext = contract.intakeData?.extraction ?? contract.intakeData ?? {};
  const meta = contract.intakeData?.meta;
  const supp = contract.intakeData?.supplement; // executed-page supplement (4b-1.5b)
  const existingNames = new Set(contacts.map((c) => c.name.trim().toLowerCase()));

  async function addContact(sc) {
    setBusyName(sc.name);
    setErr(null);
    try {
      await api(`/api/clients/${clientId}/contacts`, {
        method: "POST",
        body: JSON.stringify({
          name: sc.name,
          type: sc.type || "regular",
          ...(sc.title ? { title: sc.title } : {}),
          ...(sc.email ? { email: sc.email } : {}),
          ...(sc.phone ? { phone: sc.phone } : {}),
        }),
      });
      await onContactAdded?.();
    } catch (e) {
      setErr(e);
    } finally {
      setBusyName(null);
    }
  }

  return (
    <div className="rounded-card border border-border-default bg-app-bg">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left">
        <span className="text-[11px] uppercase text-text-muted font-medium" style={{ letterSpacing: "0.5px" }}>
          Extraction data {meta?.filename ? `· ${meta.filename}` : ""}
        </span>
        <span className="inline-flex items-center gap-2">
          <ConfidenceChip confidence={ext.confidence} />
          <span className="text-[12px] text-text-muted">{open ? "▾" : "▸"}</span>
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2.5 text-[12px]">
          {(ext.warnings?.length ?? 0) > 0 && (
            <div className="rounded border border-[#f0b890] bg-[#fbeae0] px-2.5 py-1.5 text-[#8a4b1a]">
              {ext.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1.5 text-text-secondary">
            <div><span className="text-text-muted">Client:</span> {ext.clientName || "—"}</div>
            <div><span className="text-text-muted">Type:</span> {typeLabel(ext.contractType)}</div>
            <div><span className="text-text-muted">Signed:</span> {ext.signedDate || "—"}</div>
            <div><span className="text-text-muted">Effective:</span> {ext.effectiveDate || "—"}</div>
            <div><span className="text-text-muted">End:</span> {ext.endDate || "—"}</div>
            <div><span className="text-text-muted">Renewal:</span> {ext.renewalDate || "—"}</div>
            <div><span className="text-text-muted">Term:</span> {ext.termMonths ? `${ext.termMonths} mo` : "—"}</div>
            <div><span className="text-text-muted">Auto-renew:</span> {ext.autoRenew == null ? "—" : ext.autoRenew ? "yes" : "no"}</div>
            {(ext.signerName || ext.signerEmail) && (
              <div className="col-span-2"><span className="text-text-muted">Signer:</span> {[ext.signerName, ext.signerEmail].filter(Boolean).join(" · ")}</div>
            )}
          </div>
          {ext.totalValueNote && (
            <p className="text-text-secondary italic">{ext.totalValueNote}</p>
          )}
          {supp && (
            <div className="rounded border border-border-light bg-surface px-2.5 py-2 space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase text-text-muted font-medium" style={{ letterSpacing: "0.5px" }}>
                  Executed page supplement{supp.meta?.signatureFilename ? ` · ${supp.meta.signatureFilename}` : ""}
                </span>
                <ConfidenceChip confidence={supp.extraction?.confidence} />
              </div>
              {(supp.extraction?.warnings?.length ?? 0) > 0 && (
                <div className="rounded border border-[#f0b890] bg-[#fbeae0] px-2.5 py-1.5 text-[#8a4b1a]">
                  {supp.extraction.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1.5 text-text-secondary">
                <div><span className="text-text-muted">Signed:</span> {supp.extraction?.signedDate || "—"}</div>
                <div><span className="text-text-muted">Effective:</span> {supp.extraction?.effectiveDate || "—"}</div>
                <div className="col-span-2"><span className="text-text-muted">Signer:</span> {[supp.extraction?.signerName, supp.extraction?.signerEmail].filter(Boolean).join(" · ") || "—"}</div>
              </div>
            </div>
          )}
          {(ext.contacts?.length ?? 0) > 0 && (
            <div>
              <div className="text-[11px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>
                Suggested contacts
              </div>
              <div className="space-y-1">
                {ext.contacts.map((sc, i) => {
                  const added = existingNames.has((sc.name ?? "").trim().toLowerCase());
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-text-primary">{sc.name}</span>
                      <span className="text-text-muted capitalize">({sc.type})</span>
                      {(sc.title || sc.email || sc.phone) && (
                        <span className="text-text-muted truncate">{[sc.title, sc.email, sc.phone].filter(Boolean).join(" · ")}</span>
                      )}
                      {added ? (
                        <span className="text-[11px] text-[#16a34a] font-medium">Added</span>
                      ) : (
                        <button type="button" disabled={busyName === sc.name} onClick={() => addContact(sc)}
                          className="text-[11px] text-accent hover:underline font-medium disabled:opacity-50">
                          Add as contact
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {(contract.documentUrl || contract.executedDocumentUrl) && (
            <div className="flex items-center gap-4">
              {contract.documentUrl && (
                <a href={contract.documentUrl} target="_blank" rel="noreferrer"
                  className="inline-block text-[12px] text-accent hover:underline font-medium">
                  Source document ↗
                </a>
              )}
              {contract.executedDocumentUrl && (
                <a href={contract.executedDocumentUrl} target="_blank" rel="noreferrer"
                  className="inline-block text-[12px] text-accent hover:underline font-medium">
                  Executed page ↗
                </a>
              )}
            </div>
          )}
          {err && <div className="text-[11px] text-negative">{err.message || "Something went wrong."}</div>}
        </div>
      )}
    </div>
  );
}

/**
 * "Import contract" modal — optional client pick (or let extraction detect),
 * then a PDF file-drop OR pasted text plus an optional Drive link. Submits to
 * POST /api/contracts/intake; renders the needsClient candidate picker or the
 * extraction summary before landing on the draft.
 *
 * 4b-1.5c: the needsClient step continues via the extraction passthrough
 * (continueIntake — no re-upload, no second Claude call) and can create a
 * brand-new client inline with an admin-edited name; a 409 name/slug conflict
 * surfaces the existing client as a one-click pick.
 */
function ImportContractModal({ onClose, onImported }) {
  const api = useApiAction();
  const { data: clientsData } = useApi("/api/clients");
  const clients = useMemo(
    () => (clientsData?.clients || []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [clientsData]
  );

  const [clientId, setClientId] = useState("");
  const [mode, setMode] = useState("pdf");     // "pdf" | "text"
  const [pdf, setPdf] = useState(null);        // { base64, filename, size }
  const [sigPdf, setSigPdf] = useState(null);  // optional executed page (4b-1.5b)
  const [text, setText] = useState("");
  const [driveUrl, setDriveUrl] = useState("");
  const [execUrl, setExecUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);  // intake response (needsClient or created)
  // 4b-1.5c create-from-intake: editable client name (pre-filled from the
  // extraction) + the existing client surfaced by a 409 name/slug conflict.
  const [newClientName, setNewClientName] = useState("");
  const [conflictClient, setConflictClient] = useState(null);

  const readPdf = (setter) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return setErr(new Error("PDF must be 10 MB or smaller"));
    setErr(null);
    const reader = new FileReader();
    reader.onload = () => {
      // readAsDataURL → "data:application/pdf;base64,XXXX" — strip the prefix.
      const base64 = String(reader.result).replace(/^data:[^;]+;base64,/, "");
      setter({ base64, filename: file.name, size: file.size });
    };
    reader.readAsDataURL(file);
  };
  const onFile = readPdf(setPdf);
  const onSigFile = readPdf(setSigPdf);

  async function submit(overrideClientId) {
    const chosen = overrideClientId ?? clientId;
    if (mode === "pdf" && !pdf) return setErr(new Error("Choose a PDF"));
    if (mode === "text" && !text.trim()) return setErr(new Error("Paste the contract or email text"));
    if ((mode === "pdf" ? pdf?.size ?? 0 : 0) + (sigPdf?.size ?? 0) > 10 * 1024 * 1024) {
      return setErr(new Error("The two PDFs together must be 10 MB or smaller"));
    }
    setBusy(true);
    setErr(null);
    try {
      const body = {
        ...(chosen ? { clientId: chosen } : {}),
        ...(mode === "pdf"
          ? { pdfBase64: pdf.base64, filename: pdf.filename }
          : { text: text.trim() }),
        ...(driveUrl.trim() ? { driveUrl: driveUrl.trim() } : {}),
        ...(sigPdf ? { signaturePdfBase64: sigPdf.base64, signatureFilename: sigPdf.filename } : {}),
        ...(execUrl.trim() ? { executedDriveUrl: execUrl.trim() } : {}),
      };
      const res = await api("/api/contracts/intake", { method: "POST", body: JSON.stringify(body) });
      setResult(res);
      if (res?.needsClient) setNewClientName(res.extraction?.clientName || "");
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  // 4b-1.5c fast continue: after a needsClient response, re-submit with the
  // extraction we already have (no re-upload, no second Claude call) plus
  // either the picked clientId or createClient. Metadata (filenames + Drive
  // links) still rides along so the draft records provenance.
  async function continueIntake({ clientId: chosenId, createClient } = {}) {
    if (!result?.extraction) return submit(chosenId); // defensive fallback: re-run the document
    setBusy(true);
    setErr(null);
    try {
      const body = {
        extraction: result.extraction,
        ...(chosenId ? { clientId: chosenId } : {}),
        ...(createClient ? { createClient } : {}),
        ...(mode === "pdf" && pdf ? { filename: pdf.filename } : {}),
        ...(driveUrl.trim() ? { driveUrl: driveUrl.trim() } : {}),
        ...(sigPdf ? { signatureFilename: sigPdf.filename } : {}),
        ...(execUrl.trim() ? { executedDriveUrl: execUrl.trim() } : {}),
      };
      const res = await api("/api/contracts/intake", { method: "POST", body: JSON.stringify(body) });
      setConflictClient(null);
      setResult(res);
    } catch (e) {
      // 409 with the colliding client → offer it as a one-click pick.
      setConflictClient(e.status === 409 && e.details?.existingClient ? e.details.existingClient : null);
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  const ext = result?.extraction;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-[560px] max-w-full max-h-full overflow-y-auto bg-surface rounded-card shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[15px] font-semibold text-text-primary mb-3">Import contract</h2>
        {err && <p className="text-[12px] text-negative mb-2">{err.message}</p>}

        {!result && (
          <div className="space-y-2.5">
            <Select label="Client" value={clientId} onChange={setClientId}
              options={[
                { value: "", label: "Let extraction detect the client" },
                ...clients.map((c) => ({ value: c.id, label: c.name })),
              ]} />

            <div className="flex items-center gap-1.5">
              {[{ key: "pdf", label: "Upload PDF" }, { key: "text", label: "Paste text" }].map((m) => (
                <button key={m.key} type="button" onClick={() => setMode(m.key)}
                  className={`h-7 px-2.5 rounded-card border text-[12px] font-medium transition-colors ${
                    mode === m.key
                      ? "bg-accent text-white border-accent"
                      : "border-border-default text-text-secondary hover:text-text-primary"
                  }`}>
                  {m.label}
                </button>
              ))}
            </div>

            {mode === "pdf" ? (
              <div className="rounded-card border border-dashed border-border-default p-4 text-center">
                <input type="file" accept="application/pdf" onChange={onFile}
                  className="text-[12px] text-text-secondary" />
                {pdf && <p className="mt-1.5 text-[12px] text-text-primary">{pdf.filename}</p>}
                <p className="mt-1 text-[11px] text-text-muted">Signed contract PDF, up to 10 MB. The file is read once and not stored.</p>
              </div>
            ) : (
              <div>
                <span className="block text-[11px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>
                  Contract / email text
                </span>
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8}
                  placeholder="Paste the agreement or the email thread describing it…"
                  className="w-full rounded border border-border-default bg-surface p-2 text-[12px] text-text-primary focus:outline-none focus:border-accent" />
              </div>
            )}

            {/* Executed / signature page (4b-1.5b) — legacy contracts often
                come back as a separate signed-page scan. Optional in both modes. */}
            <div className="rounded-card border border-dashed border-border-default p-3">
              <span className="block text-[11px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>
                Executed / signature page (optional)
              </span>
              <input type="file" accept="application/pdf" onChange={onSigFile}
                className="text-[12px] text-text-secondary" />
              {sigPdf && (
                <p className="mt-1.5 text-[12px] text-text-primary">
                  {sigPdf.filename}
                  <button type="button" onClick={() => setSigPdf(null)}
                    className="ml-2 text-[11px] text-text-secondary hover:text-text-primary underline">remove</button>
                </p>
              )}
              <p className="mt-1 text-[11px] text-text-muted">
                The separately returned signed page (scan or photo). Signed date and signer come from here; terms come from the agreement.
              </p>
            </div>

            <Field label="Drive link (optional — where the document lives)" value={driveUrl} onChange={setDriveUrl}
              placeholder="https://drive.google.com/…" />
            <Field label="Executed-page Drive link (optional)" value={execUrl} onChange={setExecUrl}
              placeholder="https://drive.google.com/…" />

            <div className="mt-2 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 text-[13px] text-text-secondary">Cancel</button>
              <button type="button" disabled={busy} onClick={() => submit()}
                className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">
                {busy ? "Extracting…" : "Extract"}
              </button>
            </div>
          </div>
        )}

        {result?.needsClient && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <ConfidenceChip confidence={ext?.confidence} />
              <span className="text-[12px] text-text-secondary">
                Extracted client name: <strong>{ext?.clientName || "not found"}</strong>
              </span>
            </div>
            {(ext?.warnings?.length ?? 0) > 0 && (
              <div className="rounded border border-[#f0b890] bg-[#fbeae0] px-2.5 py-1.5 text-[12px] text-[#8a4b1a]">
                {ext.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}
            <p className="text-[12px] text-text-secondary">
              {result.candidates.length === 0
                ? "No client matched — pick one below, or create a new client:"
                : "Multiple clients matched — pick the right one:"}
            </p>
            {result.candidates.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {result.candidates.map((c) => (
                  <button key={c.id} type="button" disabled={busy} onClick={() => continueIntake({ clientId: c.id })}
                    className="h-7 px-2.5 rounded-card border border-border-default text-[12px] font-medium text-text-primary hover:border-accent disabled:opacity-50">
                    {c.name}
                  </button>
                ))}
              </div>
            )}
            <Select label="All clients" value={clientId} onChange={setClientId}
              options={[{ value: "", label: "Select a client…" }, ...clients.map((c) => ({ value: c.id, label: c.name }))]} />
            <div className="mt-2 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 text-[13px] text-text-secondary">Cancel</button>
              <button type="button" disabled={busy || !clientId} onClick={() => continueIntake({ clientId })}
                className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">
                {busy ? "Working…" : "Use this client"}
              </button>
            </div>

            {/* 4b-1.5c: create the client right here and keep going. The name
                is editable on purpose — the human pass against duplicate
                clients from name variants ("Taco Bus, LLC" vs "Taco Bus"). */}
            <div className="rounded-card border border-border-default p-3">
              <Field label="Or create a new client" value={newClientName}
                onChange={(v) => { setNewClientName(v); setConflictClient(null); }}
                placeholder="Client name" />
              <p className="mt-1 text-[11px] text-text-muted">
                Check the name first — a shortened or variant name here creates a duplicate client.
              </p>
              {conflictClient && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[12px] text-text-secondary">Already exists:</span>
                  <button type="button" disabled={busy} onClick={() => continueIntake({ clientId: conflictClient.id })}
                    className="h-7 px-2.5 rounded-card border border-border-default text-[12px] font-medium text-text-primary hover:border-accent disabled:opacity-50">
                    Use {conflictClient.name}
                  </button>
                </div>
              )}
              <div className="mt-2 flex justify-end">
                <button type="button" disabled={busy || !newClientName.trim()}
                  onClick={() => continueIntake({ createClient: { name: newClientName.trim() } })}
                  className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">
                  {busy ? "Working…" : "Create client and continue"}
                </button>
              </div>
            </div>
          </div>
        )}

        {result && !result.needsClient && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <ConfidenceChip confidence={ext?.confidence} />
              <span className="text-[12px] text-text-secondary">
                Draft created for <strong>{result.contract.client?.name || "client"}</strong>
                {" — "}{result.lineItems.length} line item{result.lineItems.length === 1 ? "" : "s"}
                {ext?.lineItems && ext.lineItems.length > result.lineItems.length
                  ? `, ${ext.lineItems.length - result.lineItems.length} unmapped (kept in notes)`
                  : ""}
              </span>
            </div>
            {result.clientCreated && (
              <p className="text-[12px] text-text-secondary">
                Client created: <strong>{result.clientCreated.name}</strong> ({result.clientCreated.slug})
              </p>
            )}
            {(ext?.warnings?.length ?? 0) > 0 && (
              <div className="rounded border border-[#f0b890] bg-[#fbeae0] px-2.5 py-1.5 text-[12px] text-[#8a4b1a]">
                {ext.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}
            <p className="text-[12px] text-text-muted">
              Review the draft (dates, line items, suggested contacts in the Extraction data panel), then Activate it.
            </p>
            <div className="mt-2 flex justify-end">
              <button type="button" onClick={() => onImported(result.contract.id)}
                className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium">
                Review draft
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * "Add executed page" modal (4b-1.5b) — supplement an existing draft/active
 * contract with the separately returned signature page (file drop or pasted
 * text + optional Drive link). Calls POST /api/contracts/:id/intake-supplement,
 * shows what was applied, then refreshes the detail via onApplied.
 */
function SupplementModal({ contractId, onClose, onApplied }) {
  const api = useApiAction();
  const [mode, setMode] = useState("pdf");    // "pdf" | "text"
  const [pdf, setPdf] = useState(null);       // { base64, filename }
  const [text, setText] = useState("");
  const [execUrl, setExecUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null); // { contract, extraction }

  function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return setErr(new Error("PDF must be 10 MB or smaller"));
    setErr(null);
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result).replace(/^data:[^;]+;base64,/, "");
      setPdf({ base64, filename: file.name });
    };
    reader.readAsDataURL(file);
  }

  async function submit() {
    if (mode === "pdf" && !pdf) return setErr(new Error("Choose a PDF"));
    if (mode === "text" && !text.trim()) return setErr(new Error("Paste the signature-page text"));
    setBusy(true);
    setErr(null);
    try {
      const body = {
        ...(mode === "pdf"
          ? { signaturePdfBase64: pdf.base64, signatureFilename: pdf.filename }
          : { text: text.trim() }),
        ...(execUrl.trim() ? { executedDriveUrl: execUrl.trim() } : {}),
      };
      const res = await api(`/api/contracts/${contractId}/intake-supplement`, {
        method: "POST", body: JSON.stringify(body),
      });
      setResult(res);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  const ext = result?.extraction;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-[480px] max-w-full max-h-full overflow-y-auto bg-surface rounded-card shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[15px] font-semibold text-text-primary mb-3">Add executed page</h2>
        {err && <p className="text-[12px] text-negative mb-2">{err.message}</p>}

        {!result ? (
          <div className="space-y-2.5">
            <div className="flex items-center gap-1.5">
              {[{ key: "pdf", label: "Upload scan" }, { key: "text", label: "Paste text" }].map((m) => (
                <button key={m.key} type="button" onClick={() => setMode(m.key)}
                  className={`h-7 px-2.5 rounded-card border text-[12px] font-medium transition-colors ${
                    mode === m.key
                      ? "bg-accent text-white border-accent"
                      : "border-border-default text-text-secondary hover:text-text-primary"
                  }`}>
                  {m.label}
                </button>
              ))}
            </div>

            {mode === "pdf" ? (
              <div className="rounded-card border border-dashed border-border-default p-4 text-center">
                <input type="file" accept="application/pdf" onChange={onFile}
                  className="text-[12px] text-text-secondary" />
                {pdf && <p className="mt-1.5 text-[12px] text-text-primary">{pdf.filename}</p>}
                <p className="mt-1 text-[11px] text-text-muted">Signature-page scan (PDF), up to 10 MB. The file is read once and not stored.</p>
              </div>
            ) : (
              <div>
                <span className="block text-[11px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>
                  Signature-page text
                </span>
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6}
                  placeholder="Paste the signature block / executed-page text…"
                  className="w-full rounded border border-border-default bg-surface p-2 text-[12px] text-text-primary focus:outline-none focus:border-accent" />
              </div>
            )}

            <Field label="Executed-page Drive link (optional)" value={execUrl} onChange={setExecUrl}
              placeholder="https://drive.google.com/…" />

            <div className="mt-2 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 text-[13px] text-text-secondary">Cancel</button>
              <button type="button" disabled={busy} onClick={submit}
                className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">
                {busy ? "Extracting…" : "Extract & apply"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <ConfidenceChip confidence={ext?.confidence} />
              <span className="text-[12px] text-text-secondary">Execution facts applied.</span>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[12px] text-text-secondary">
              <div><span className="text-text-muted">Signed:</span> {ext?.signedDate || "—"}</div>
              <div><span className="text-text-muted">Effective:</span> {ext?.effectiveDate || "—"}</div>
              <div className="col-span-2"><span className="text-text-muted">Signer:</span> {[ext?.signerName, ext?.signerEmail].filter(Boolean).join(" · ") || "—"}</div>
            </div>
            {(ext?.warnings?.length ?? 0) > 0 && (
              <div className="rounded border border-[#f0b890] bg-[#fbeae0] px-2.5 py-1.5 text-[12px] text-[#8a4b1a]">
                {ext.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}
            <div className="mt-2 flex justify-end">
              <button type="button" onClick={onApplied}
                className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium">
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NewContractModal({ onClose, onCreated }) {
  const api = useApiAction();
  const { data: clientsData } = useApi("/api/clients");
  // Change #4: the manual editor's catalog picker for the nested line items.
  const { data: catalogData } = useApi("/api/services?active=true");
  const clients = useMemo(
    () => (clientsData?.clients || []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [clientsData]
  );

  const [form, setForm] = useState({
    clientId: "", status: "active", type: "", startDate: "", effectiveDate: "",
    endDate: "", renewalDate: "", termMonths: "12", autoRenew: true, // BMG policy defaults; end/renewal derive at activation
    continuationType: "", // Change #2: required — a new contract is never Unclassified
    billingContactId: "", signerContactId: "", notes: "",
  });
  const [items, setItems] = useState([]); // local line items (temp ids) until POST
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Contact roster for the pickers — only once a client is chosen.
  const { data: contactsData, reload: reloadContacts } = useApi(
    `/api/clients/${form.clientId}/contacts`,
    { enabled: !!form.clientId }
  );
  const contacts = contactsData?.contacts || [];

  async function submit() {
    if (!form.clientId) return setErr(new Error("Pick a client"));
    if (!form.continuationType) return setErr(new Error("Choose how this agreement continues"));
    setBusy(true);
    setErr(null);
    try {
      const body = {
        clientId: form.clientId,
        status: form.status,
        ...(form.type ? { type: form.type } : {}),
        ...(form.startDate ? { startDate: form.startDate } : {}),
        ...(form.effectiveDate ? { effectiveDate: form.effectiveDate } : {}),
        ...(form.endDate ? { endDate: form.endDate } : {}),
        ...(form.renewalDate ? { renewalDate: form.renewalDate } : {}),
        ...(form.termMonths ? { termMonths: Number(form.termMonths) } : {}),
        ...(form.autoRenew ? { autoRenew: true } : {}),
        continuationType: form.continuationType,
        ...(form.billingContactId ? { billingContactId: form.billingContactId } : {}),
        ...(form.signerContactId ? { signerContactId: form.signerContactId } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
        ...(items.length ? { lineItems: items.map(({ id, ...li }) => li) } : {}),
      };
      await api("/api/contracts", { method: "POST", body: JSON.stringify(body) });
      await onCreated();
    } catch (e) {
      setErr(e);
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-[640px] max-w-full max-h-full overflow-y-auto bg-surface rounded-card shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[15px] font-semibold text-text-primary mb-3">New Contract</h2>
        {err && <p className="text-[12px] text-negative mb-2">{err.message}</p>}
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            <Select label="Client" required value={form.clientId}
              onChange={(v) => setForm((f) => ({ ...f, clientId: v, billingContactId: "", signerContactId: "" }))}
              options={[{ value: "", label: "Select a client…" }, ...clients.map((c) => ({ value: c.id, label: c.name }))]} />
            <Select label="Status" value={form.status} onChange={set("status")}
              options={[
                { value: "active", label: "Active (existing contract)" },
                { value: "draft", label: "Draft" },
              ]} />
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <Select label="Type" value={form.type} onChange={set("type")}
              options={[{ value: "", label: "—" }, ...CONTRACT_TYPE_OPTIONS]} />
            <Field label="Term (months)" type="number" value={form.termMonths} onChange={set("termMonths")} />
          </div>
          {/* Change #2 (Codex #1): the API REQUIRES continuationType on create and
              submit() validates it — without this control the form could never be
              submitted at all. The placeholder is not a selectable value: a new
              contract is classified by a person, never left Unclassified. */}
          <Select label="How this agreement continues" required value={form.continuationType}
            onChange={set("continuationType")}
            options={[{ value: "", label: "Select…" }, ...CONTINUATION_OPTIONS]} />
          <p className="text-[11px] text-text-muted -mt-1.5">
            Read it off the signed agreement. “Continues until cancelled” keeps billing and work
            running past the end date until cancelled in writing.
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Start date" type="date" value={form.startDate} onChange={set("startDate")} />
            <Field label="Effective date" type="date" value={form.effectiveDate} onChange={set("effectiveDate")} />
            <Field label="End date" type="date" value={form.endDate} onChange={set("endDate")} />
            <Field label="Renewal date" type="date" value={form.renewalDate} onChange={set("renewalDate")} />
          </div>
          {form.clientId ? (
            <div className="grid grid-cols-2 gap-2.5">
              <ContactSelect label="Billing contact" contacts={contacts} value={form.billingContactId}
                onChange={set("billingContactId")} clientId={form.clientId} defaultType="billing"
                onContactAdded={() => reloadContacts({ silent: true })} />
              <ContactSelect label="Signer" contacts={contacts} value={form.signerContactId}
                onChange={set("signerContactId")} clientId={form.clientId} defaultType="signer"
                onContactAdded={() => reloadContacts({ silent: true })} />
            </div>
          ) : (
            <p className="text-[11px] text-text-muted">Pick a client to choose billing/signer contacts.</p>
          )}
          <label className="flex items-center gap-2 text-[12px] text-text-secondary cursor-pointer">
            <input type="checkbox" checked={form.autoRenew}
              onChange={(e) => set("autoRenew")(e.target.checked)} />
            Auto-renews
          </label>
          <Field label="Terms / notes" value={form.notes} onChange={set("notes")} />

          <div>
            <div className="text-[11px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>
              Line items
            </div>
            <LineItemEditor
              items={items}
              services={catalogData?.services ?? []}
              onCreate={(values) => setItems((xs) => [...xs, { id: `tmp-${Date.now()}`, ...values }])}
              // Change #4 correction (Task 6 review): the editor's update payload
              // carries explicit nulls for the fee fields the chosen type does not
              // use — right for an API-mode PUT, wrong for these LOCAL items, which
              // are posted later as nested creates that accept no nulls. Omit
              // null/undefined (a loose != null keeps a legitimate 0) and REPLACE the
              // line rather than merge, so a cleared fee field or a previous catalog
              // serviceId cannot survive from the old row.
              onUpdate={(id, values) => setItems((xs) => xs.map((x) => (x.id === id
                ? { id, ...Object.fromEntries(Object.entries(values).filter(([, v]) => v != null)) }
                : x)))}
              onRemove={(id) => setItems((xs) => xs.filter((x) => x.id !== id))}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[13px] text-text-secondary">Cancel</button>
          <button type="button" disabled={busy || !form.clientId} onClick={submit}
            className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">
            {busy ? "Saving…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Click-to-sign (4b-4): send/resend and mark-signed modals
// ─────────────────────────────────────────────────────────────────────────

function SendContractModal({ contract, onClose, onSend }) {
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const resend = contract.status === "sent";
  async function submit() {
    if (submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      await onSend(message.trim() ? { message: message.trim() } : {});
    } catch (e) {
      setErr(e.message || "Send failed");
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-[480px] max-w-full bg-surface rounded-card shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[15px] font-semibold text-text-primary mb-1">{resend ? "Resend for signature" : "Send for signature"}</h2>
        <p className="text-[12px] text-text-muted mb-3">
          Emails the agreement PDF and a signing link to <strong>{contract.signerContact?.name}</strong> ({contract.signerContact?.email}). The link is valid 30 days and only that address can sign.
          {resend ? " Resending replaces the previous link." : " Line items and terms lock once sent."}
        </p>
        <label className="block text-[10px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>Personal message (optional)</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={1000} className="w-full rounded border border-border-default px-2.5 py-2 text-[13px] bg-surface" />
        {err && <p className="text-[12px] text-negative mt-2">{err}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" disabled={submitting} onClick={onClose} className="px-3 py-1.5 text-[13px] text-text-secondary disabled:opacity-50">Cancel</button>
          <button type="button" disabled={submitting} onClick={submit} className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">{submitting ? "Sending…" : resend ? "Resend" : "Send"}</button>
        </div>
      </div>
    </div>
  );
}

function MarkSignedModal({ contract, onClose, onConfirm }) {
  const [name, setName] = useState(contract.signerContact?.name || "");
  const [email, setEmail] = useState(contract.signerContact?.email || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const ready = name.trim() && /\S+@\S+\.\S+/.test(email) && /^\d{4}-\d{2}-\d{2}$/.test(date);
  async function confirmIt() {
    if (submitting) return;
    setErr(null);
    if (file && !(file.type === "application/pdf" || /\.pdf$/i.test(file.name))) return setErr("Please choose a PDF file");
    if (file && file.size > 10 * 1024 * 1024) return setErr("PDF must be 10 MB or smaller");
    setSubmitting(true);
    try {
      const body = { signedByName: name.trim(), signedByEmail: email.trim(), signedDate: date };
      if (file) {
        const b64 = await new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result).split(",")[1]); r.onerror = reject; r.readAsDataURL(file); });
        body.signedPdfBase64 = b64; body.filename = file.name;
      }
      await onConfirm(body);
    } catch (e) {
      setErr(e.message || "Could not record the signature");
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-[480px] max-w-full bg-surface rounded-card shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[15px] font-semibold text-text-primary mb-1">Mark signed</h2>
        <p className="text-[12px] text-text-muted mb-3">Records an agreement signed outside Prism (Google eSignature, paper). This activates the contract, emails the executed copy, and creates the signer's portal login.</p>
        <div className="space-y-2.5">
          <Field label="Signed by (name)" required value={name} onChange={setName} />
          <Field label="Signed by (email)" type="email" required value={email} onChange={setEmail} />
          <Field label="Signed date" type="date" required value={date} onChange={setDate} />
          <div>
            <label className="block text-[10px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>Executed PDF (optional, ≤10 MB)</label>
            <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-[12px]" />
          </div>
        </div>
        {err && <p className="text-[12px] text-negative mt-2">{err}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" disabled={submitting} onClick={onClose} className="px-3 py-1.5 text-[13px] text-text-secondary disabled:opacity-50">Cancel</button>
          <button type="button" disabled={!ready || submitting} onClick={confirmIt} className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">{submitting ? "Recording…" : "Record signature"}</button>
        </div>
      </div>
    </div>
  );
}
